import { useSearch } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { AtSign, Bell, FileText, Mail, ShieldCheck, Send } from 'lucide-react'

import { SettingsSidebar, type SettingsSidebarItem } from '@/components/shared/settings-sidebar'
import { BetaBadge } from '@/components/shared/beta-badge'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'
import { usePermissions } from '@/hooks/use-permissions'
import { organizationStore } from '@/stores/organization.store'

export const EMAIL_SECTIONS = [
  'senders',
  'saved-templates',
  'automated-emails',
  'blocked-recipients',
  'connected-inbox',
  'my-notifications',
] as const

export type EmailSection = (typeof EMAIL_SECTIONS)[number]

export const DEFAULT_EMAIL_SECTION: EmailSection = 'senders'

/** The one Email section every org member may open: their own notification choices. */
export const MEMBER_EMAIL_SECTION: EmailSection = 'my-notifications'

export function isEmailSection(value: unknown): value is EmailSection {
  return typeof value === 'string' && (EMAIL_SECTIONS as readonly string[]).includes(value)
}

export function EmailSidebar() {
  const search = useSearch({ from: '/_app/_settings/email' })
  const currentSection = isEmailSection(search.section) ? search.section : DEFAULT_EMAIL_SECTION
  const { hasFeature: hasEmailInbox } = useHasEmailInboxFeature()
  const selectedOrganizationId = useStore(organizationStore, (s) => s.selectedOrganizationId)
  const { can } = usePermissions(selectedOrganizationId || '')
  const canManageEmail = can('CommsView') || can('SettingsManage')

  // My notifications is every member's own opt-out; the rest needs Comms or
  // Settings, matching the route guard.
  const sections: Array<SettingsSidebarItem<EmailSection> & { visible: boolean }> = [
    { title: 'Senders', value: 'senders', icon: Mail, visible: canManageEmail },
    { title: 'Saved Templates', value: 'saved-templates', icon: FileText, visible: canManageEmail },
    { title: 'Automated Emails', value: 'automated-emails', icon: Send, visible: canManageEmail },
    {
      title: 'Connected inbox',
      value: 'connected-inbox',
      icon: AtSign,
      badge: <BetaBadge />,
      visible: canManageEmail && hasEmailInbox,
    },
    { title: 'My notifications', value: MEMBER_EMAIL_SECTION, icon: Bell, visible: true },
    {
      title: 'Blocked Recipients',
      value: 'blocked-recipients',
      icon: ShieldCheck,
      badge: <BetaBadge />,
      visible: canManageEmail && hasEmailInbox,
    },
  ]
  const navItems = sections.filter((section) => section.visible)

  return (
    <SettingsSidebar
      mode="link"
      items={navItems}
      currentSection={currentSection}
      routePath="/email"
      className="h-full w-64 border-r"
    />
  )
}
